// CategorySubcategoryView.js

import React, { useEffect, useState } from "react";
import { Card, Row, Col, Form } from "react-bootstrap";
import Select from "react-select";
import {
  FiGrid,
  FiLayers,
  FiImage,
  FiCheck,
} from "react-icons/fi";

import styles from "./CategorySubcategoryView.module.css";

import { getImageUrl } from "../../MIS/Global";

const CategorySubcategoryView = ({
  categoryList = [],
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
  setFieldTouched,
}) => {
  const [preview, setPreview] = useState("");

  // ================= IMAGE PREVIEW =================

  useEffect(() => {
    let imageUrl = "";

    // NO IMAGE
    if (!values?.img_path) {
      setPreview("");
      return;
    }

    // OLD IMAGE STRING FROM API
    if (typeof values.img_path === "string") {
      setPreview(getImageUrl(values.img_path));
      return;
    }

    // NEW FILE IMAGE
    if (
      values.img_path &&
      typeof values.img_path === "object" &&
      values.img_path instanceof File
    ) {
      try {
        imageUrl = URL.createObjectURL(values.img_path);

        setPreview(imageUrl);
      } catch (error) {
        console.log("Preview Error:", error);

        setPreview("");
      }
    }

    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [values?.img_path]);

  // ================= OPTIONS =================

  const typeOptions = [
    {
      value: "category",
      label: "Category",
    },
    {
      value: "subcategory",
      label: "Sub Category",
    },
  ];

  const categoryOptions =
    categoryList?.map((item) => ({
      value: item?.id,
      label: item?.name,
    })) || [];

  // ================= IMAGE CHANGE =================

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    // NO FILE
    if (!file) return;

    // INVALID FILE
    if (!(file instanceof File)) return;

    setFieldValue("img_path", file);

    setFieldTouched("img_path", true, true);
  };

  // ================= SELECT STYLE =================

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "44px",
      borderRadius: "12px",
      borderColor: state.isFocused
        ? "#6366f1"
        : "#e5e7eb",
      boxShadow: state.isFocused
        ? "0 0 0 3px rgba(99,102,241,0.10)"
        : "none",
      "&:hover": {
        borderColor: "#6366f1",
      },
      fontSize: "14px",
    }),

    valueContainer: (base) => ({
      ...base,
      paddingLeft: "38px",
    }),

    placeholder: (base) => ({
      ...base,
      color: "#9ca3af",
    }),

    menu: (base) => ({
      ...base,
      zIndex: 9999,
      borderRadius: "12px",
      overflow: "hidden",
    }),
  };

  return (
    <Card className={styles.mainCard}>
      <Card.Body className={styles.cardBody}>
        {/* HEADER */}

        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.iconBox}>
              {values?.type?.value === "category" ? (
                <FiGrid />
              ) : (
                <FiLayers />
              )}
            </div>

            <div>
              <h5 className={styles.title}>
                Category Management
              </h5>

              <p className={styles.subtitle}>
                Manage category & subcategory
              </p>
            </div>
          </div>

          <div
            className={`${styles.status} ${
              values?.is_active
                ? styles.active
                : styles.inactive
            }`}
          >
            <FiCheck />

            {values?.is_active
              ? "Active"
              : "Inactive"}
          </div>
        </div>

        {/* FORM */}

        <Form autoComplete="off">
          <Row className="g-3">
            {/* TYPE */}

            <Col md={4} xs={12}>
              <Form.Label className={styles.label}>
                Type
              </Form.Label>

              <div className={styles.selectWrapper}>
                <div className={styles.inputIcon}>
                  {values?.type?.value === "category" ? (
                    <FiGrid />
                  ) : (
                    <FiLayers />
                  )}
                </div>

                <Select
                  value={values?.type}
                  options={typeOptions}
                  styles={customSelectStyles}
                  isSearchable={false}
                  onChange={(selected) => {
                    setFieldValue("type", selected);

                    setFieldValue(
                      "category",
                      null
                    );
                  }}
                  onBlur={() =>
                    setFieldTouched(
                      "type",
                      true
                    )
                  }
                />
              </div>

              {touched?.type &&
                errors?.type && (
                  <div className="text-danger mt-1 small">
                    {errors?.type}
                  </div>
                )}
            </Col>

            {/* CATEGORY */}

            {values?.type?.value ===
              "subcategory" && (
              <Col md={4} xs={12}>
                <Form.Label
                  className={styles.label}
                >
                  Main Category
                </Form.Label>

                <div
                  className={
                    styles.selectWrapper
                  }
                >
                  <div
                    className={
                      styles.inputIcon
                    }
                  >
                    <FiGrid />
                  </div>

                  <Select
                    value={values?.category}
                    options={
                      categoryOptions
                    }
                    styles={
                      customSelectStyles
                    }
                    placeholder="Select Category"
                    onChange={(
                      selected
                    ) =>
                      setFieldValue(
                        "category",
                        selected
                      )
                    }
                    onBlur={() =>
                      setFieldTouched(
                        "category",
                        true
                      )
                    }
                  />
                </div>

                {touched?.category &&
                  errors?.category && (
                    <div className="text-danger mt-1 small">
                      {
                        errors?.category
                      }
                    </div>
                  )}
              </Col>
            )}

            {/* NAME */}

            <Col
              md={
                values?.type?.value ===
                "subcategory"
                  ? 4
                  : 8
              }
              xs={12}
            >
              <Form.Label
                className={styles.label}
              >
                Name
              </Form.Label>

              <div
                className={
                  styles.inputWrapper
                }
              >
                <div
                  className={
                    styles.inputIcon
                  }
                >
                  {values?.type?.value ===
                  "category" ? (
                    <FiGrid />
                  ) : (
                    <FiLayers />
                  )}
                </div>

                <Form.Control
                  type="text"
                  name="name"
                  value={
                    values?.name || ""
                  }
                  onChange={
                    handleChange
                  }
                  onBlur={() =>
                    setFieldTouched(
                      "name",
                      true
                    )
                  }
                  placeholder={`Enter ${
                    values?.type?.value ===
                    "category"
                      ? "Category"
                      : "Sub Category"
                  } Name`}
                  className={
                    styles.formControl
                  }
                  isInvalid={
                    touched?.name &&
                    !!errors?.name
                  }
                />
              </div>

              {touched?.name &&
                errors?.name && (
                  <div className="text-danger mt-1 small">
                    {errors?.name}
                  </div>
                )}
            </Col>

            {/* STATUS */}

            <Col md={4} xs={12}>
              <Form.Label
                className={styles.label}
              >
                Status
              </Form.Label>

              <div
                className={
                  styles.switchBox
                }
              >
                <Form.Check
                  type="switch"
                  name="is_active"
                  checked={
                    values?.is_active ||
                    false
                  }
                  onChange={
                    handleChange
                  }
                  label={
                    values?.is_active
                      ? "Active"
                      : "Inactive"
                  }
                />
              </div>
            </Col>

            {/* IMAGE */}

            <Col md={8} xs={12}>
              <Form.Label
                className={styles.label}
              >
                Image
              </Form.Label>

              <label
                className={
                  styles.uploadBox
                }
              >
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={
                    handleImageChange
                  }
                />

                {preview &&
                typeof preview ===
                  "string" ? (
                  <img
                    src={preview}
                    alt="preview"
                    className={
                      styles.previewImage
                    }
                  />
                ) : (
                  <div
                    className={
                      styles.uploadContent
                    }
                  >
                    <FiImage
                      className={
                        styles.uploadIcon
                      }
                    />

                    <span>
                      Upload Image
                    </span>
                  </div>
                )}
              </label>

              {touched?.img_path &&
                errors?.img_path && (
                  <div className="text-danger mt-1 small">
                    {
                      errors?.img_path
                    }
                  </div>
                )}
            </Col>
          </Row>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default CategorySubcategoryView;